import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Switch,
  Collapse,
  Divider
} from '@mui/material';
import {
  Cookie as CookieIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useCustomTheme } from '../../context/ThemeContext';

const CookieConsentBanner = ({ onAccept, onReject, onCustomize }) => {
  const { currentTheme } = useCustomTheme();
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: true,
    analytics: true,
    marketing: false
  });

  const handleToggle = (category) => {
    if (category === 'necessary') return; // Always required
    setPreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleAcceptAll = () => {
    onAccept({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    });
  };

  const handleAcceptSelected = () => {
    onCustomize(preferences);
  };

  const handleRejectAll = () => {
    onReject({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    });
  };

  const cookieCategories = [
    {
      name: 'necessary',
      label: 'Necessary Cookies',
      description: 'Required for authentication, security, and basic site functionality.',
      required: true
    },
    {
      name: 'functional',
      label: 'Functional Cookies',
      description: 'Remember your preferences like language, theme, and dashboard settings.',
      required: false
    },
    {
      name: 'analytics',
      label: 'Analytics Cookies',
      description: 'Help us understand how you use our platform to improve your experience.',
      required: false
    },
    {
      name: 'marketing',
      label: 'Marketing Cookies',
      description: 'Used for personalized content and relevant service recommendations.',
      required: false
    }
  ];

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: { xs: 0, md: 20 },
        left: { xs: 0, md: 20 },
        right: { xs: 0, md: 'auto' },
        maxWidth: { xs: '100%', md: 480 },
        zIndex: 9999,
        borderRadius: { xs: 0, md: 2 },
        border: `1px solid ${currentTheme?.palette?.mode === 'dark' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(102, 126, 234, 0.2)'}`,
        boxShadow: currentTheme?.palette?.mode === 'dark'
          ? '0 8px 32px rgba(0,0,0,0.5)'
          : '0 8px 32px rgba(0,0,0,0.15)'
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <Box
            sx={{
              p: 1,
              mr: 2,
              borderRadius: 1,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CookieIcon sx={{ color: 'white', fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              Cookie Preferences
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              We use cookies to enhance your experience, remember your preferences, and improve our platform.
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleRejectAll}
            sx={{ ml: 1 }}
            aria-label="Close and reject"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Customize Button */}
        <Button
          fullWidth
          variant="text"
          onClick={() => setShowDetails(!showDetails)}
          endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            mb: 2,
            justifyContent: 'space-between',
            color: 'primary.main',
            textTransform: 'none',
            fontWeight: 500
          }}
        >
          Customize Cookie Settings
        </Button>

        {/* Cookie Categories */}
        <Collapse in={showDetails}>
          <Box sx={{ mb: 2, maxHeight: 300, overflowY: 'auto' }}>
            {cookieCategories.map((category, index) => (
              <Box key={category.name}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1.5
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="semibold">
                      {category.label}
                      {category.required && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{
                            ml: 1,
                            px: 1,
                            py: 0.25,
                            borderRadius: 0.5,
                            bgcolor: 'success.light',
                            color: 'success.dark',
                            fontWeight: 600
                          }}
                        >
                          Always Active
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {category.description}
                    </Typography>
                  </Box>
                  <Switch
                    checked={preferences[category.name]}
                    onChange={() => handleToggle(category.name)}
                    disabled={category.required}
                    size="small"
                    color="primary"
                  />
                </Box>
                {index < cookieCategories.length - 1 && <Divider />}
              </Box>
            ))}
          </Box>
        </Collapse>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleRejectAll}
            sx={{
              borderColor: 'rgba(102, 126, 234, 0.3)',
              color: 'text.secondary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'rgba(102, 126, 234, 0.05)'
              }
            }}
          >
            Reject All
          </Button>
          {showDetails && (
            <Button
              fullWidth
              variant="outlined"
              onClick={handleAcceptSelected}
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: 'rgba(102, 126, 234, 0.05)'
                }
              }}
            >
              Save Selection
            </Button>
          )}
          <Button
            fullWidth
            variant="contained"
            onClick={handleAcceptAll}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
              }
            }}
          >
            Accept All
          </Button>
        </Box>

        {/* Privacy Policy Link */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 2, textAlign: 'center' }}
        >
          Read our{' '}
          <Typography
            component="a"
            href="/legal/cookies"
            variant="caption"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Cookie Policy
          </Typography>
          {' and '}
          <Typography
            component="a"
            href="/legal/privacy"
            variant="caption"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Privacy Policy
          </Typography>
        </Typography>
      </Box>
    </Paper>
  );
};

export default CookieConsentBanner;
